import { useCallback, useEffect, useState } from 'react';
import { useWebSocket } from '../../contexts/WebSocketContext';
import api from '../../services/api';
import type {
  NewsBeatWebSocketEvent,
  NewsBodySource,
  NewsRunLog,
  NewsRunStep,
} from '../../types';

/**
 * How this briefing came to look the way it does.
 *
 * Three phases, in the order the run performs them: what we asked GNews and
 * what came back, which article bodies had to be fetched, what the agent did
 * with each one. Live while a run is in flight and unchanged afterwards — the
 * steps are persisted (`NewsBriefing.run_log`), so this is not a view that
 * empties when you close the tab.
 *
 * The per-article table underneath is DERIVED from columns the run already
 * wrote, not from the log. BODY column first, because "we judged this on two
 * sentences" explains more bad briefings than anything else here.
 */

const SOURCE_LABEL: Record<NewsBodySource, string> = {
  GNEWS_FULL: 'full text',
  FETCHED: 'fetched',
  SNIPPET_ONLY: 'snippet only',
};

const SOURCE_TONE: Record<NewsBodySource, string> = {
  GNEWS_FULL: 'text-gray-500 dark:text-gray-400',
  FETCHED: 'text-emerald-700 dark:text-emerald-400',
  SNIPPET_ONLY: 'text-amber-700 dark:text-amber-400',
};

const STATUS_LABEL: Record<string, string> = {
  CANDIDATE: 'not reached',
  FILTERED_OUT: 'dropped',
  GROUPED: 'in a story',
  MUTED: 'muted',
};

const KIND_TONE: Record<string, string> = {
  query: 'text-gray-600 dark:text-gray-300',
  warning: 'text-amber-700 dark:text-amber-400',
  failed: 'text-red-700 dark:text-red-400',
  rejected: 'text-gray-500 dark:text-gray-400',
  muted: 'text-gray-500 dark:text-gray-400',
  elided: 'text-gray-400 dark:text-gray-500 italic',
};

function time(at: string): string {
  try {
    return new Date(at).toLocaleTimeString();
  } catch {
    return '';
  }
}

/** The GNews request, rendered as the call it was. */
function SearchRequest({ data }: { data: Record<string, any> }) {
  const request = data.request;
  if (!request) return null;
  // `queries` is plural: long term lists are split across several searches.
  const queries: string[] = request.queries ?? (request.query ? [request.query] : []);
  return (
    <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
      <dt>{queries.length > 1 ? `queries (${queries.length})` : 'query'}</dt>
      <dd className="font-mono break-all text-gray-700 dark:text-gray-300 max-h-32 overflow-y-auto space-y-1">
        {queries.length === 0 && '(empty)'}
        {queries.map((q: string, i: number) => (
          <div key={i}>{q}</div>
        ))}
      </dd>
      <dt>window</dt>
      <dd>
        {request.window_from?.slice(0, 16).replace('T', ' ')} →{' '}
        {request.window_to?.slice(0, 16).replace('T', ' ')}
      </dd>
      <dt>locale</dt>
      <dd>
        {request.language}/{request.country} · max {request.max_articles}
      </dd>
    </dl>
  );
}

function Step({ step }: { step: NewsRunStep }) {
  return (
    <li className="flex gap-2 text-xs">
      <span className="tabular-nums text-gray-400 dark:text-gray-500 shrink-0">
        {time(step.at)}
      </span>
      <div className="min-w-0">
        <span className={KIND_TONE[step.kind] ?? 'text-gray-700 dark:text-gray-300'}>
          {step.message}
        </span>
        <SearchRequest data={step.data} />
        {step.data.query && (
          <div className="mt-0.5 font-mono text-[11px] text-gray-400 dark:text-gray-500 break-all">
            {step.data.query}
          </div>
        )}
        {step.data.total_available > 0 && (
          <span className="ml-1 text-[11px] text-gray-400 dark:text-gray-500">
            ({step.data.pages_fetched} page
            {step.data.pages_fetched === 1 ? '' : 's'} fetched)
          </span>
        )}
      </div>
    </li>
  );
}

export default function BriefingRunLog({ briefingId }: { briefingId: number }) {
  const { subscribe } = useWebSocket();
  const [log, setLog] = useState<NewsRunLog | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLog(await api.getNewsBriefingRunLog(briefingId));
    } catch {
      setLog(null);
    }
  }, [briefingId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return subscribe((raw: unknown) => {
      const event = raw as NewsBeatWebSocketEvent;
      if (event?.event_type !== 'news_beat' || event.briefing_id !== briefingId) {
        return;
      }
      // Append the step we were handed rather than refetching: a run writes one
      // per article, and a round trip each would be a request storm at exactly
      // the busiest moment.
      const step = event.log_step;
      if (event.status === 'log' && step) {
        setLog((prev) => (prev ? { ...prev, steps: [...prev.steps, step] } : prev));
        return;
      }
      // The derived half (bodies, per-article outcomes) only settles at the
      // end, so that one is worth a refetch.
      if (['finished', 'failed', 'cancelled'].includes(event.status)) load();
    });
  }, [subscribe, briefingId, load]);

  if (!log || (log.steps.length === 0 && log.articles.length === 0)) return null;

  const snippetOnly = log.body_sources.SNIPPET_ONLY ?? 0;
  const byPhase = log.phases
    .map((phase) => ({
      ...phase,
      steps: log.steps.filter((s) => s.phase === phase.key),
    }))
    .filter((p) => p.steps.length > 0);

  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left"
      >
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          How this briefing was built
        </span>
        <span className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          {snippetOnly > 0 && (
            <span className="text-amber-700 dark:text-amber-400">
              {snippetOnly} judged on a snippet
            </span>
          )}
          <span>{log.steps.length} steps</span>
          <span className="text-gray-400">{open ? '▴' : '▾'}</span>
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-4 border-t border-gray-200 dark:border-gray-700 pt-3">
          {byPhase.map((phase) => (
            <div key={phase.key}>
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
                {phase.label}
              </h4>
              <ul className="space-y-1">
                {phase.steps.map((step, i) => (
                  <Step key={`${phase.key}-${i}`} step={step} />
                ))}
              </ul>
            </div>
          ))}

          {log.articles.length > 0 && (
            <div>
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
                Every article considered ({log.articles.length})
              </h4>
              {/* Wide content scrolls in its own box rather than widening the page. */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-400 dark:text-gray-500">
                      <th className="font-normal py-1 pr-3">Body</th>
                      <th className="font-normal py-1 pr-3">Outcome</th>
                      <th className="font-normal py-1">Article</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.articles.map((a) => (
                      <tr
                        key={a.article_id}
                        className="border-t border-gray-100 dark:border-gray-800 align-top"
                      >
                        <td className={`py-1 pr-3 whitespace-nowrap ${SOURCE_TONE[a.body_source]}`}>
                          {SOURCE_LABEL[a.body_source]}
                          {a.body_source === 'SNIPPET_ONLY' && a.missing_chars
                            ? ` (−${a.missing_chars} chars)`
                            : ''}
                        </td>
                        <td className="py-1 pr-3 whitespace-nowrap text-gray-600 dark:text-gray-300">
                          {STATUS_LABEL[a.status] ?? a.status.toLowerCase()}
                          {a.rejected_by_attribute && (
                            <span className="text-gray-400 dark:text-gray-500">
                              {' '}
                              by <code>{a.rejected_by_attribute}</code>
                            </span>
                          )}
                        </td>
                        <td className="py-1 text-gray-700 dark:text-gray-300">
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:underline"
                          >
                            {a.title}
                          </a>
                          {a.source_name && (
                            <span className="text-gray-400 dark:text-gray-500">
                              {' '}
                              · {a.source_name}
                            </span>
                          )}
                          {a.reject_reason && (
                            <p className="text-gray-400 dark:text-gray-500">
                              {a.reject_reason}
                            </p>
                          )}
                          {/* The facts the clustering compared. Without these
                              `cluster_key` is a bare number and "why are these
                              two one story?" has no answer on the page. */}
                          {a.tags?.length > 0 && (
                            <p className="mt-0.5 flex flex-wrap gap-1">
                              {a.tags.map((t) => (
                                <span
                                  key={`${t.dimension}:${t.value}`}
                                  className="px-1 rounded bg-gray-100 dark:bg-gray-700 text-[10px] text-gray-500 dark:text-gray-400"
                                  title={t.dimension}
                                >
                                  {t.value}
                                </span>
                              ))}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
